'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { formatShortDate } from '@/lib/c2c/format'
import { cn } from '@/lib/utils'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon } from '@/components/c2c/icons/calendar-icon'

import { calendarLocale, formatCalendarCaption } from './calendar-locale'

interface SearchDatePickerProps {
    label: string
    value?: string // YYYY-MM-DD
    onChange: (date: string) => void
    placeholder?: string
    hasError?: boolean
    minDate?: string // YYYY-MM-DD — earliest selectable date
    onTodayClick?: () => void
    grouped?: boolean
}

/** Parse YYYY-MM-DD at noon to avoid timezone shifts */
function parseDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day, 12, 0, 0)
}

/** Format Date to YYYY-MM-DD */
function toISODate(date: Date): string {
    const y = String(date.getFullYear())
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

export function SearchDatePicker({
    label,
    value,
    onChange,
    placeholder,
    hasError,
    minDate,
    onTodayClick,
    grouped,
}: SearchDatePickerProps) {
    const t = useTranslations('searchWidget')
    const locale = useLocale()
    const [open, setOpen] = useState(false)

    const selected = value ? parseDate(value) : undefined
    const calendar = calendarLocale(locale)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-12 w-full items-center bg-white px-3 text-left text-sm whitespace-nowrap transition-colors',
                        grouped
                            ? ''
                            : cn(
                                  'rounded-[7px] border',
                                  hasError ? 'border-red-500' : 'border-[#d9dee5]',
                              ),
                        value ? 'text-[#2e3033]' : 'text-[#8c939c]',
                    )}
                    aria-label={label}
                >
                    <CalendarIcon className="mr-2 h-5 w-5 text-[#2e3033]" />
                    {value ? formatShortDate(value, locale) : (placeholder ?? label)}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    locale={calendar.locale}
                    weekStartsOn={calendar.weekStartsOn}
                    // Names and accessible labels come from the bundle (RD-11); only the
                    // caption is overridden, because it is the one string with no width
                    // constraint and the bundle's pattern omits the `de` Spanish needs.
                    formatters={{
                        formatCaption: (month) => formatCalendarCaption(month, locale),
                    }}
                    selected={selected}
                    onSelect={(date) => {
                        if (date) {
                            onChange(toISODate(date))
                        }
                        setOpen(false)
                    }}
                    disabled={{ before: minDate ? parseDate(minDate) : new Date() }}
                    className="[&_[data-selected-single=true]]:!bg-[#ff5c5c]"
                    footer={
                        <button
                            type="button"
                            onClick={() => {
                                if (onTodayClick) {
                                    onTodayClick()
                                } else {
                                    onChange(toISODate(new Date()))
                                }
                                setOpen(false)
                            }}
                            className="w-full pb-2 text-center text-sm text-[#00bfd5] hover:underline"
                        >
                            {t('today')}
                        </button>
                    }
                />
            </PopoverContent>
        </Popover>
    )
}
